import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { FolderKanban, CheckCircle, Users, TrendingUp, Clock, Target } from 'lucide-react';

const ProjectManagers = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What should project managers include in their performance review?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Include projects delivered on time and budget, stakeholder satisfaction scores, team utilization rates, risk mitigation successes, and process improvements implemented. Quantify with specific metrics: 'Delivered 12 projects with 92% on-time rate and 5% average budget underrun.'"
        }
      },
      {
        "@type": "Question",
        "name": "How do project managers demonstrate value beyond project delivery?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Document process improvements, tools implemented, team development, cross-functional relationship building, and lessons learned initiatives. Show how you've made the organization better at delivering projects, not just your individual project success."
        }
      },
      {
        "@type": "Question",
        "name": "What metrics matter for project management performance reviews?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Key metrics include: on-time delivery rate, budget variance, stakeholder satisfaction, scope change management, resource utilization, and team velocity/productivity. Also track certifications earned, methodologies implemented, and risk management effectiveness."
        }
      },
      {
        "@type": "Question",
        "name": "How often should project managers document their achievements?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Document at project milestones and retrospectives, at minimum monthly. Track successful risk mitigations, stakeholder feedback, and team wins as they happen. Use a tool like BragBox to capture accomplishments in real-time for easy review preparation."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Performance Review Tips for Project Managers | BragBox</title>
        <meta name="description" content="Ace your project management performance review with actionable tips. Learn how to showcase delivery success, stakeholder relationships, and leadership beyond the Gantt chart." />
        <meta name="keywords" content="project manager performance review, PM self-evaluation, project delivery metrics, project management career growth, PMP review tips" />
        <link rel="canonical" href="https://bragbox.ai/tips/project-managers" />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-[#1F2A44] via-[#2d3a5c] to-[#1F2A44]">
        {/* Header */}
        <header className="bg-[#1F2A44]/90 backdrop-blur-sm border-b border-white/10 sticky top-0 z-50">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-[#C4A35A] to-[#d4b36a] rounded-lg flex items-center justify-center">
                <span className="text-[#1F2A44] font-bold text-sm">B</span>
              </div>
              <span className="text-white font-semibold text-lg">BragBox</span>
            </Link>
            <Link
              to="/login?signup=true"
              className="bg-[#C4A35A] hover:bg-[#d4b36a] text-[#1F2A44] px-4 py-2 rounded-lg font-medium transition-colors"
            >
              Start free
            </Link>
          </div>
        </header>

        {/* Hero Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-[#C4A35A]/20 text-[#C4A35A] px-4 py-2 rounded-full text-sm mb-6">
              <FolderKanban className="w-4 h-4" />
              Project Management Career Tips
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Performance Review Tips for Project Managers
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Your projects succeed because of the chaos you prevent, not just the milestones you hit. Learn how to document the invisible work of coordination, risk management, and stakeholder alignment.
            </p>
          </div>
        </section>

        {/* Pain Points Section */}
        <section className="py-12 px-4 bg-white/5">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">Why PMs Struggle with Self-Evaluations</h2>
            <p className="text-gray-300 mb-6">
              You navigated a scope change that could have derailed the entire project. You mediated a conflict between two departments that was blocking progress. You identified a risk three months early and prevented a $200K cost overrun. But when review time comes, leadership just asks "Did the project deliver on time?"
            </p>
            <p className="text-gray-300">
              Project management success is often invisible precisely because you're good at it. When you prevent problems, there's no fire to put out. When you keep stakeholders aligned, there's no dramatic escalation. Your best work looks like nothing happening at all.
            </p>
          </div>
        </section>

        {/* Tips Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-12 text-center">10 Actionable Tips for Your PM Review</h2>
            
            <div className="space-y-8">
              {/* Tip 1 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Target className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">1. Quantify Delivery Performance</h3>
                    <p className="text-gray-300 mb-3">
                      Go beyond "projects delivered." Show on-time percentages, budget variance, and scope adherence across your portfolio.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Delivered 8 projects totaling $2.4M with 94% on-time rate and 3% average budget underrun. Managed portfolio of 12 concurrent workstreams across 4 departments with zero critical path delays."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 2 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">2. Document Risk Mitigation Wins</h3>
                    <p className="text-gray-300 mb-3">
                      Risks you identified and mitigated are achievements. Track potential impacts you prevented through proactive risk management.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Identified vendor capacity risk 8 weeks before go-live, enabling backup vendor onboarding that prevented 3-week delay. Early dependency tracking avoided $180K in potential rework costs on integration project."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 3 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">3. Track Stakeholder Satisfaction</h3>
                    <p className="text-gray-300 mb-3">
                      Happy stakeholders mean repeat business and career growth. Document feedback, survey scores, and positive relationships built.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Achieved 4.7/5.0 average stakeholder satisfaction across all projects. Three executive sponsors specifically requested me for future initiatives. Received commendation from VP of Operations for communication excellence."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 4 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">4. Highlight Scope Management Success</h3>
                    <p className="text-gray-300 mb-3">
                      Managing scope creep is a skill. Document how you handled change requests while protecting timelines and budgets.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Managed 23 change requests across projects, with 85% properly scoped and funded before implementation. Prevented scope creep on flagship project by implementing formal change control, maintaining original timeline despite 15% functionality increase."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 5 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">5. Document Process Improvements</h3>
                    <p className="text-gray-300 mb-3">
                      Templates, workflows, and methodologies you've improved benefit the entire organization. Track efficiency gains you've driven.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Implemented Agile ceremonies for marketing team, improving sprint velocity by 34%. Created project intake template now used organization-wide, reducing project kickoff time from 2 weeks to 3 days."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 6 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">6</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">6. Capture Team Development Contributions</h3>
                    <p className="text-gray-300 mb-3">
                      Growing team capabilities shows leadership. Document mentorship, training delivered, and team members who improved under your guidance.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Mentored 2 associate PMs, both promoted to PM within 12 months. Delivered internal training on stakeholder management attended by 25 colleagues. Created PM onboarding guide reducing new PM ramp time by 40%."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 7 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">7</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">7. Track Resource Management Excellence</h3>
                    <p className="text-gray-300 mb-3">
                      Efficient resource utilization shows business acumen. Document utilization rates, capacity planning wins, and cost savings.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Maintained 87% team utilization while avoiding burnout (zero unplanned attrition). Optimized resource allocation across 3 projects, reducing contractor spend by $45K through better internal capacity planning."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 8 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">8</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">8. Document Cross-Functional Coordination</h3>
                    <p className="text-gray-300 mb-3">
                      Getting multiple departments aligned is sophisticated work. Track dependencies managed, conflicts resolved, and cross-team collaboration facilitated.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Coordinated integration project spanning IT, Marketing, Sales, and Finance with 47 dependencies managed. Facilitated resolution of 8 cross-departmental conflicts that were blocking progress. Established RACI framework now used across all enterprise projects."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 9 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">9</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">9. Highlight Professional Development</h3>
                    <p className="text-gray-300 mb-3">
                      Certifications, training, and new methodologies learned show commitment to growth. Document what you've invested in developing yourself.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Earned PMP certification and CSM credential. Completed 40 PDUs in advanced stakeholder management and Agile at scale. Applied scaled Agile framework to enterprise initiative, improving cross-team coordination 45%."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 10 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">10</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">10. Capture Wins at Project Milestones</h3>
                    <p className="text-gray-300 mb-3">
                      Don't wait for project close to document achievements. Capture wins at each phase gate, retrospective, and milestone.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Pro Tip:</p>
                      <p className="text-gray-300 text-sm">After every sprint review, stakeholder meeting, or risk mitigation win, take 30 seconds to log it in BragBox. At project close, review your entries and update with final metrics. Your review prep will be almost done before you start.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Metrics Examples Section */}
        <section className="py-16 px-4 bg-white/5">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Key Metrics Project Managers Should Track</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-[#C4A35A] font-semibold mb-3">Delivery Metrics</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• On-time delivery rate</li>
                  <li>• Budget variance</li>
                  <li>• Scope adherence</li>
                  <li>• Quality metrics/defect rates</li>
                </ul>
              </div>
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-[#C4A35A] font-semibold mb-3">Stakeholder Metrics</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• Stakeholder satisfaction scores</li>
                  <li>• Communication effectiveness</li>
                  <li>• Executive sponsor feedback</li>
                  <li>• Repeat engagement rate</li>
                </ul>
              </div>
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-[#C4A35A] font-semibold mb-3">Team Metrics</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• Resource utilization</li>
                  <li>• Team velocity/productivity</li>
                  <li>• Team satisfaction scores</li>
                  <li>• Attrition rates</li>
                </ul>
              </div>
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-[#C4A35A] font-semibold mb-3">Risk & Process</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• Risks identified vs realized</li>
                  <li>• Change request management</li>
                  <li>• Process improvement impact</li>
                  <li>• Lessons learned implemented</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Frequently Asked Questions</h2>
            <div className="space-y-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">What should project managers include in their performance review?</h3>
                <p className="text-gray-300">Include projects delivered on time and budget, stakeholder satisfaction scores, team utilization rates, risk mitigation successes, and process improvements implemented. Quantify with specific metrics: "Delivered 12 projects with 92% on-time rate and 5% average budget underrun."</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">How do project managers demonstrate value beyond project delivery?</h3>
                <p className="text-gray-300">Document process improvements, tools implemented, team development, cross-functional relationship building, and lessons learned initiatives. Show how you've made the organization better at delivering projects, not just your individual project success.</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">What metrics matter for project management performance reviews?</h3>
                <p className="text-gray-300">Key metrics include: on-time delivery rate, budget variance, stakeholder satisfaction, scope change management, resource utilization, and team velocity/productivity. Also track certifications earned, methodologies implemented, and risk management effectiveness.</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">How often should project managers document their achievements?</h3>
                <p className="text-gray-300">Document at project milestones and retrospectives, at minimum monthly. Track successful risk mitigations, stakeholder feedback, and team wins as they happen. Use a tool like BragBox to capture accomplishments in real-time for easy review preparation.</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 bg-gradient-to-r from-[#C4A35A]/20 to-[#C4A35A]/10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Deliver Your Best Performance Review</h2>
            <p className="text-xl text-gray-300 mb-8">
              BragBox helps project managers capture delivery wins, stakeholder feedback, and process improvements throughout the year.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/login?signup=true"
                className="inline-block bg-[#C4A35A] hover:bg-[#d4b36a] text-[#1F2A44] px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
              >
                Start free
              </Link>
              <Link to="/login" className="text-gray-300 underline underline-offset-4 hover:text-white transition-colors">
                Already have an account? Log in.
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 px-4 border-t border-white/10">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-[#C4A35A] to-[#d4b36a] rounded-lg flex items-center justify-center">
                  <span className="text-[#1F2A44] font-bold text-sm">B</span>
                </div>
                <span className="text-white font-semibold">BragBox</span>
              </Link>
              <div className="flex gap-6 text-gray-400 text-sm">
                <Link to="/" className="hover:text-white transition-colors">Home</Link>
                <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
                <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
                <Link to="/login" className="hover:text-white transition-colors">Login</Link>
              </div>
              <p className="text-gray-500 text-sm">© 2026 BragBox. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ProjectManagers;
